import { matchPath } from "react-router-dom";
import { ElementType } from "react";

export type AppRoute = {
  TITLE: string;
  MAINTITLE?: string;
  ICON?: ElementType;
  KEY: number;
  PATH: string;
  DISABLED?: boolean;
  ELEMENT?: ElementType;
  DIRECT?: boolean;
  DEFAULT_ROUTE?: boolean;
  SUBNAV?: AppRoute[];
  SUBSUBNAV?: AppRoute[];
};

/** Keep only routes marked DEFAULT_ROUTE (and parents that still have visible children). */
export function filterDefaultRoutes(routes: AppRoute[]): AppRoute[] {
  return routes
    .map((route) => {
      if (route.SUBNAV?.length) {
        const filteredSubnav = filterDefaultRoutes(route.SUBNAV);
        if (filteredSubnav.length === 0) {
          return route.DEFAULT_ROUTE ? { ...route, SUBNAV: undefined } : null;
        }
        return { ...route, SUBNAV: filteredSubnav };
      }
      return route.DEFAULT_ROUTE ? route : null;
    })
    .filter((route): route is AppRoute => route !== null);
}

const buildFullPath = (parentPath: string, routePath: string): string => {
  const cleanParent = parentPath.endsWith("/") ? parentPath.slice(0, -1) : parentPath;
  const cleanRoute = routePath.startsWith("/") ? routePath.slice(1) : routePath;
  return `${cleanParent}/${cleanRoute}`.replace(/\/+/g, "/");
};

export function collectRoutePaths(
  routes: AppRoute[],
  parentPath = "/client"
): string[] {
  const paths: string[] = [];

  for (const route of routes) {
    const fullPath = buildFullPath(parentPath, route.PATH);
    paths.push(fullPath);

    if (route.SUBNAV?.length) {
      paths.push(...collectRoutePaths(route.SUBNAV, fullPath));
    }
  }

  return paths;
}

/** Paths reachable while subscription payment is overdue. */
export const SUBSCRIPTION_PAYMENT_PATHS = [
  "/client/pay",
  "/client/sms-topup",
  "/client/payment/success",
  "/client/payment/cancel",
  "/client/payment/error",
];

export function isSubscriptionPaymentPath(pathname: string): boolean {
  return SUBSCRIPTION_PAYMENT_PATHS.some((path) => {
    if (matchPath({ path, end: true }, pathname)) return true;
    return matchPath({ path: `${path}/*`, end: false }, pathname) !== null;
  });
}

export function isPathAllowed(
  pathname: string,
  routes: AppRoute[],
  extraPaths: string[] = SUBSCRIPTION_PAYMENT_PATHS
): boolean {
  const allowedPaths = [...collectRoutePaths(routes), ...extraPaths];

  return allowedPaths.some((path) => {
    if (matchPath({ path, end: true }, pathname)) return true;
    return matchPath({ path: `${path}/*`, end: false }, pathname) !== null;
  });
}
